import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetEquipmentProjectsQuery } from "../../../store/appApi";
import { selectActiveProjectId } from "../../../store/projectSlice";
import { createAssetApi, updateAssetApi } from "../../../services/assets";

export const ASSET_CONDITIONS = ["New", "Good", "Fair", "Poor", "Damaged", "Disposed"];

const empty = {
  assetName: "",
  category: "",
  assetCode: "",
  quantity: 1,
  unitCost: "",
  purchaseDate: "",
  vendor: "",
  location: "",
  assignedTo: "",
  condition: "Good",
  notes: "",
};

// Turn an asset from the list into form values (dates → yyyy-mm-dd for <input type="date">).
const toForm = (a) => {
  const f = { ...empty };
  Object.keys(empty).forEach((k) => {
    if (a[k] !== undefined && a[k] !== null) f[k] = a[k];
  });
  f.purchaseDate = a.purchaseDate ? moment(a.purchaseDate).format("YYYY-MM-DD") : "";
  return f;
};

// Used for both /assets/add and /assets/edit/:id. In edit mode the asset comes
// in via router state from the Asset List (there's no single-asset GET).
const AddAsset = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = useLocation().state?.asset;
  const isEdit = Boolean(id);
  const activeProjectId = useSelector(selectActiveProjectId);
  const { data } = useGetEquipmentProjectsQuery();
  const projects = data?.projects || [];

  const [form, setForm] = useState(() => (editing ? toForm(editing) : empty));
  const [projectId, setProjectId] = useState(
    () => (editing && (editing.projectId?._id || editing.projectId)) || "",
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const effectiveProject = projectId || activeProjectId || "";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.assetName.trim()) {
      Swal.fire("Required", "Asset name is required.", "warning");
      return;
    }
    if (!isEdit && !effectiveProject) {
      Swal.fire("Required", "Select a project.", "warning");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateAssetApi(id, form);
      } else {
        await createAssetApi({ ...form, projectId: effectiveProject });
      }
      dispatch(appApi.util.invalidateTags(["Assets"]));
      await Swal.fire({
        icon: "success",
        title: isEdit ? "Asset updated" : "Asset added",
        timer: 1200,
        showConfirmButton: false,
      });
      navigate("/assets/list");
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || (isEdit ? "Could not update asset." : "Could not add asset."),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const input = (label, key, props = {}) => (
    <div className="col-md-4 mb-3">
      <label className="form-label">{label}</label>
      <input className="form-control" value={form[key]} onChange={set(key)} {...props} />
    </div>
  );

  const title = isEdit ? "Edit Asset" : "Add Asset";

  // Opened /assets/edit/:id directly (e.g. after a refresh) — no data to edit.
  if (isEdit && !editing) {
    return (
      <>
        <PageTitle activeMenu={title} motherMenu="Assets" />
        <div className="card">
          <div className="card-body text-center text-muted py-4">
            Open the asset from the <Link to="/assets/list">Asset List</Link> to edit it.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={title} motherMenu="Assets" />
      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">{title}</h4>
        </div>
        <div className="card-body">
          <form onSubmit={submit}>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Project *</label>
                <select
                  className="form-control"
                  value={effectiveProject}
                  disabled={isEdit}
                  title={isEdit ? "An asset's project can't be changed" : undefined}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  <option value="">Select project</option>
                  {projects.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.projectName}
                    </option>
                  ))}
                </select>
              </div>
              {input("Asset name *", "assetName", { placeholder: "e.g. Office chair, Laptop" })}
              {input("Category", "category", { placeholder: "Furniture / IT / Tools…" })}
              {input("Asset code / tag", "assetCode")}
              {input("Quantity", "quantity", { type: "number", min: 0 })}
              {input("Unit cost (₹)", "unitCost", { type: "number", min: 0 })}
              {input("Purchase date", "purchaseDate", { type: "date" })}
              {input("Vendor", "vendor")}
              {input("Location on site", "location")}
              {input("Assigned to", "assignedTo", { placeholder: "Person / team" })}
              <div className="col-md-4 mb-3">
                <label className="form-label">Condition</label>
                <select className="form-control" value={form.condition} onChange={set("condition")}>
                  {ASSET_CONDITIONS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes} onChange={set("notes")} />
              </div>
            </div>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Update asset" : "Save asset"}
            </button>
            {isEdit && (
              <Link to="/assets/list" className="btn btn-light ms-2">
                Cancel
              </Link>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default AddAsset;
